import React, { useState } from 'react'
import { useApp } from '../context/AppContext'

export default function TaskModal({ task, defaultEventId, onClose }) {
  const { addTask, updateTask, toggleTask, addComment, events, currentUser } = useApp()
  const [comment, setComment] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [form, setForm] = useState(task ? {
    eventId: task.eventId,
    title: task.title,
    description: task.description,
    priority: task.priority,
    assignedTo: task.assignedTo,
    deadline: task.deadline || '',
    deadlineTime: task.deadlineTime || '',
    orderDate: task.orderDate || '',
    price: task.price || '',
    quantity: task.quantity || '1',
    link: task.link || '',
  } : {
    eventId: defaultEventId || (events[0] && events[0].id) || '',
    title: '',
    description: '',
    priority: 'high',
    assignedTo: currentUser.name,
    deadline: '',
    deadlineTime: '',
    orderDate: '',
    price: '',
    quantity: '1',
    link: '',
  })

  const isView = !!task && !isEditing
  const selectedEvent = events.find(e => e.id === form.eventId)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEditing) {
      const updates = {
        title: form.title,
        description: form.description,
        priority: form.priority,
        assignedTo: form.assignedTo,
        quantity: parseInt(form.quantity) || 1,
      }
      if (form.deadline) updates.deadline = form.deadline
      if (form.deadlineTime) updates.deadlineTime = form.deadlineTime
      if (form.orderDate) updates.orderDate = form.orderDate
      if (form.price) updates.price = parseFloat(form.price)
      if (form.link) updates.link = form.link
      updateTask(task.id, updates)
      setIsEditing(false)
    } else {
      const event = events.find(ev => ev.id === form.eventId)
      addTask(form.eventId, {
        eventId: form.eventId,
        eventName: event ? event.name : '',
        eventColor: event ? event.color : '#6c63ff',
        title: form.title,
        description: form.description,
        priority: form.priority,
        assignedTo: form.assignedTo,
        assignedToId: currentUser.id,
        deadline: form.deadline || undefined,
        deadlineTime: form.deadlineTime || undefined,
        orderDate: form.orderDate || undefined,
        price: form.price ? parseFloat(form.price) : undefined,
        quantity: parseInt(form.quantity) || 1,
        link: form.link || undefined,
      })
      onClose()
    }
  }

  const handleComment = () => {
    if (!comment.trim() || !task) return
    addComment(task.id, comment, currentUser.name)
    setComment('')
  }

  const priorityLabel = { high: 'høj', medium: 'middel', low: 'lav' }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <div className="modal-title">{isView ? task.title : 'Opret en ny opgave'}</div>
            {isView && <div className="modal-subtitle">tildelt {task.assignedTo}{task.eventName ? ` · ${task.eventName}` : ''}</div>}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {isView && !isEditing ? (
            <div>
              {/* Badges */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                <span className={`pill pill-${task.priority}`}>{priorityLabel[task.priority]} prioritet</span>
                {task.deadline && <span className="pill pill-soon">deadline {task.deadline}{task.deadlineTime ? ` kl. ${task.deadlineTime}` : ''}</span>}
                {task.done && <span className="pill pill-done">klaret</span>}
              </div>

              {task.description && (
                <div className="input-group">
                  <label className="input-label">detaljer</label>
                  <div style={{ background: '#f7f5ff', borderRadius: 12, padding: '10px 12px', fontSize: 12, lineHeight: 1.6, color: '#1a1a2e' }}>{task.description}</div>
                </div>
              )}

              <div className="form-grid-2" style={{ marginBottom: 12 }}>
                {task.orderDate && <div><label className="input-label">bestillingsdato</label><p style={{ fontSize: 12, fontWeight: 600 }}>{task.orderDate}</p></div>}
                {task.deadline && <div><label className="input-label">deadline</label><p style={{ fontSize: 12, fontWeight: 600 }}>{task.deadline}</p></div>}
                {task.price && <div><label className="input-label">budget</label><p style={{ fontSize: 12, fontWeight: 600 }}>{task.price.toLocaleString('da-DK')} kr</p></div>}
              </div>

              {task.link && (
                <div className="input-group">
                  <label className="input-label">Link</label>
                  <a href={task.link} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#6c63ff', fontWeight: 500 }}>{task.link}</a>
                </div>
              )}

              {/* Comments */}
              <div className="input-group">
                <label className="input-label">Kommentarer</label>
                {task.comments && task.comments.map(c => (
                  <div key={c.id} style={{ background: '#f7f5ff', borderRadius: 12, padding: '10px 12px', marginBottom: 8 }}>
                    <p style={{ fontSize: 10, fontWeight: 600, color: '#6c63ff', marginBottom: 3 }}>{c.author}</p>
                    <p style={{ fontSize: 12, color: '#1a1a2e' }}>{c.text}</p>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <input className="input" style={{ flex: 1 }} placeholder="skriv en kommentar..." value={comment} onChange={e => setComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleComment()} />
                  <button className="btn-primary" onClick={handleComment} style={{ flexShrink: 0 }}>Send</button>
                </div>
              </div>

              {/* Done toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 8 }}>
                <div
                  className={`checkbox ${task.done ? 'checked' : ''}`}
                  style={{ width: 18, height: 18 }}
                  onClick={() => toggleTask(task.id)}
                >
                  {task.done && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <span style={{ fontSize: 12, fontWeight: 500 }}>Marker som færdig</span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="input-group">
                <label className="input-label">Tildel til event</label>
                <select className="input" value={form.eventId} onChange={e => setForm({ ...form, eventId: e.target.value })}>
                  {events && events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
                </select>
              </div>
              <div className="input-group">
                <label className="input-label">Opgavetitel</label>
                <input required className="input" placeholder="hvad skal gøres?" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Prioritet</label>
                <div className="prio-selector">
                  {['high', 'medium', 'low'].map(p => (
                    <button key={p} type="button" onClick={() => setForm({ ...form, priority: p })}
                      className={`prio-btn ${form.priority === p ? `active-${p}` : ''}`}>
                      {p === 'high' ? 'høj' : p === 'medium' ? 'middel' : 'lav'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="input-group">
                <label className="input-label">Detaljer</label>
                <textarea className="input" rows={2} style={{ resize: 'none' }} placeholder="beskriv opgaven..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="form-grid-2">
                <div className="input-group">
                  <label className="input-label">Ansvarlig</label>
                  <select className="input" style={{ appearance: 'none', cursor: 'pointer' }} value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}>
                    <option value={currentUser.name}>{currentUser.name} (dig selv)</option>
                    {selectedEvent?.members?.filter(m => m.name !== currentUser.name).map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
                <div className="input-group">
                  <label className="input-label">Deadline (dato)</label>
                  <input className="input" type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} />
                </div>
              </div>
              <div className="input-group">
                <label className="input-label">Deadline (tidspunkt)</label>
                <input className="input" type="time" value={form.deadlineTime} onChange={e => setForm({ ...form, deadlineTime: e.target.value })} />
              </div>
              {!isEditing && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button type="button" onClick={onClose} className="modal-cancel" style={{ flex: 1 }}>Annuller</button>
                  <button type="submit" className="modal-confirm" style={{ flex: 1 }}>Opret opgave</button>
                </div>
              )}
            </form>
          )}
        </div>

        {isView && (
          <div className="modal-footer">
            <button className="modal-cancel" onClick={onClose}>Luk</button>
            <button className="modal-confirm" onClick={() => setIsEditing(true)}>Rediger</button>
          </div>
        )}
        {isEditing && (
          <div className="modal-footer">
            <button className="modal-cancel" onClick={() => { setIsEditing(false); setForm({ eventId: task.eventId, title: task.title, description: task.description, priority: task.priority, assignedTo: task.assignedTo, deadline: task.deadline || '', deadlineTime: task.deadlineTime || '', orderDate: task.orderDate || '', price: task.price || '', quantity: task.quantity || '1', link: task.link || '' }) }}>annuller</button>
            <button className="modal-confirm" onClick={handleSubmit}>gem ændringer</button>
          </div>
        )}
      </div>
    </div>
  )
}
